/* eslint-disable max-len */
import {
  Text,
  View,
  Image,
  StyleSheet,
  Document,
  Page
} from '@react-pdf/renderer';
import React, {useState, useRef} from 'react';
import {Editor, NodeEntry, BaseRange} from 'slate';
import {Editable} from 'slate-react';

import {ImageCropperProps} from '../../pages/reportBuilder/constants/ReportBuilderInterfce';
import {ElementRenderer} from '../../pages/reportBuilder/renderers/ElementRenderer';
import {LeafRenderer} from '../../pages/reportBuilder/renderers/LeafRenderer';
import {getFrameworkColor} from '../../pages/reportBuilder/utils/formattingUtils';
import {useConfigStore} from '../../store/reportStore/configStore';

import '../../styles/_reportComponents.scss';

// --- START OF ImageCropper.tsx ---
export const ImageCropper: React.FC<ImageCropperProps> = ({
  imageSrc,
  onCrop,
  onCancel
}) => {
  const [crop, setCrop] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState<{x: number; y: number} | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const getRelPos = (e: React.MouseEvent | MouseEvent) => {
    if (!containerRef.current) return {x: 0, y: 0};
    const rect = containerRef.current.getBoundingClientRect();

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const pos = getRelPos(e);
    setStartPos(pos);
    setIsDrawing(true);
    setCrop({x: pos.x, y: pos.y, width: 0, height: 0});
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !startPos) return;
    const pos = getRelPos(e);
    const x = Math.min(pos.x, startPos.x);
    const y = Math.min(pos.y, startPos.y);
    const width = Math.abs(pos.x - startPos.x);
    const height = Math.abs(pos.y - startPos.y);
    setCrop({x, y, width, height});
  };

  const handleCropConfirm = () => {
    if (!crop || !imageRef.current || crop.width === 0 || crop.height === 0) {
      onCrop(imageSrc);

      return;
    }
    const canvas = document.createElement('canvas');
    const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(
      imageRef.current,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );
    onCrop(canvas.toDataURL('image/png'));
  };

  return (
    <div
      className="image-cropper-overlay"
      onMouseUp={() => {
        setIsDrawing(false);
      }}
    >
      <div
        className="cropper-container"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
      >
        <img
          ref={imageRef}
          src={imageSrc}
          alt="Crop Source"
          className="source-image"
          draggable={false}
        />
        {crop && (
          <div
            className="crop-box"
            style={{
              left: crop.x,
              top: crop.y,
              width: crop.width,
              height: crop.height
            }}
          />
        )}
        <div
          className="crop-actions"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
        >
          <button className="crop-btn secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="crop-btn primary" onClick={handleCropConfirm}>
            {crop && crop.width > 0 ? 'Crop & Insert' : 'Insert Full'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- START OF PaginatedDocument.tsx ---
interface PaginatedDocumentProps {
  editor: Editor;
  readOnly?: boolean;
  pageOrientation?: 'portrait' | 'landscape';
  pageMargins?: any;
  watermark?: any;
  decorate?: (entry: NodeEntry) => BaseRange[];
  headerFooterEditMode?: boolean;
  onEditorMouseDownCapture?: (e: React.MouseEvent) => void;
  onEditorKeyDown?: (e: React.KeyboardEvent) => void;
}

export const PaginatedDocument: React.FC<PaginatedDocumentProps> = ({
  readOnly = false,
  pageOrientation = useConfigStore.getState().pageOrientation,
  pageMargins = useConfigStore.getState().pageMargins,
  watermark,
  decorate,
  headerFooterEditMode = false,
  onEditorMouseDownCapture,
  onEditorKeyDown
}) => {
  return (
    <div className="paginated-document-container">
      <Editable
        renderElement={(props) => (
          <ElementRenderer
            {...props}
            headerFooterEditMode={headerFooterEditMode}
            watermark={watermark}
            pageOrientation={pageOrientation}
            pageMargins={pageMargins}
          />
        )}
        renderLeaf={LeafRenderer}
        readOnly={readOnly}
        decorate={decorate ?? (() => [])}
        onMouseDownCapture={onEditorMouseDownCapture}
        onKeyDown={onEditorKeyDown}
        className="paginated-editable"
      />
    </div>
  );
};

// --- START OF PdfBlock.tsx ---
const styles0 = StyleSheet.create({
  page: {padding: 48, fontSize: 12, fontFamily: 'Helvetica', lineHeight: 1.5},
  paragraph: {marginBottom: 8},
  headingOne: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 12,
    marginTop: 18
  },
  headingTwo: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 10,
    marginTop: 16
  },
  blockQuote: {
    borderLeftWidth: 4,
    borderLeftColor: '#dadce0',
    paddingLeft: 12,
    marginBottom: 12,
    fontStyle: 'italic',
    color: '#555'
  },
  table: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#ccc'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ccc'
  },
  tableCell: {
    flex: 1,
    padding: 6,
    borderRightWidth: 1,
    borderRightColor: '#ccc'
  },
  imageContainer: {marginBottom: 12, alignItems: 'center'},
  image: {objectFit: 'contain'},
  questionBox: {
    padding: 0,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff'
  },
  questionHeader: {
    padding: '4 8',
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold'
  },
  questionContent: {padding: 12},
  questionKey: {fontSize: 9, color: '#666', marginBottom: 4},
  questionText: {fontSize: 12, fontWeight: 'bold'},
  progressBar: {
    height: 16,
    borderWidth: 1,
    borderColor: '#000',
    backgroundColor: '#fff',
    marginBottom: 12,
    position: 'relative'
  }
});

const PdfLeaf: React.FC<{leaf: any; children: any}> = ({leaf, children}) => {
  const style: any = {};
  if (leaf.bold) style.fontWeight = 'bold';
  if (leaf.italic) style.fontStyle = 'italic';
  if (leaf.underline) style.textDecoration = 'underline';
  if (leaf.fontSize) style.fontSize = leaf.fontSize;
  if (leaf.color) style.color = leaf.color;
  if (leaf.highlight) style.backgroundColor = leaf.highlight;

  return <Text style={style}>{children}</Text>;
};

const PdfBlock: React.FC<{node: any}> = ({node}) => {
  if (node.text !== undefined) {
    return <PdfLeaf leaf={node}>{node.text}</PdfLeaf>;
  }

  const children = node.children
    ? node.children.map((child: any, i: number) => (
        <PdfBlock key={i} node={child} />
      ))
    : null;
  const alignStyle: any = node.align ? {textAlign: node.align} : {};

  const hasBlockChildren = node.children?.some((c: any) =>
    ['image', 'table', 'framework-question', 'percentage-bar'].includes(c.type)
  );

  if (hasBlockChildren) {
    return <View style={[{marginBottom: 10}, alignStyle]}>{children}</View>;
  }

  switch (node.type) {
    case 'heading-one':
      return <Text style={[styles0.headingOne, alignStyle]}>{children}</Text>;
    case 'heading-two':
      return <Text style={[styles0.headingTwo, alignStyle]}>{children}</Text>;
    case 'block-quote':
      return <Text style={[styles0.blockQuote, alignStyle]}>{children}</Text>;
    case 'paragraph':
      const isEmpty =
        !node.children ||
        (node.children.length === 1 && node.children[0].text === '');

      return (
        <Text style={[styles0.paragraph, alignStyle]}>
          {isEmpty ? ' ' : children}
        </Text>
      );
    case 'spacer':
      const size = node.size === 'sm' ? 8 : node.size === 'lg' ? 24 : 16;

      return <View style={{height: size, width: '100%'}} />;
    case 'image':
      return (
        <View style={[styles0.imageContainer, alignStyle]}>
          <Image
            style={[
              styles0.image,
              {height: node.height || 200, width: node.width || '100%'}
            ]}
            src={node.url}
          />
        </View>
      );
    case 'framework-question':
      const headerColor = getFrameworkColor(node.framework);

      return (
        <View
          style={[styles0.questionBox, {borderColor: headerColor}]}
          wrap={false}
        >
          <View
            style={[styles0.questionHeader, {backgroundColor: headerColor}]}
          >
            <Text style={{color: 'white', fontWeight: 'bold'}}>
              {(node.framework || 'QUESTION').toUpperCase()}
            </Text>
          </View>
          <View style={styles0.questionContent}>
            {node.key && <Text style={styles0.questionKey}>{node.key}</Text>}
            <Text style={styles0.questionText}>{node.question}</Text>
          </View>
        </View>
      );
    case 'header-wrapper':
      const isThreeColumn = node.headerType === 'three-columns';

      return (
        <View style={[{marginBottom: 10, width: '100%'}, alignStyle]}>
          {isThreeColumn
            ? node.children.map((child: any, i: number) => (
                <View
                  key={i}
                  style={{
                    flex: 1,
                    alignItems:
                      i === 0 ? 'flex-start' : i === 1 ? 'center' : 'flex-end'
                  }}
                >
                  <PdfBlock node={child} />
                </View>
              ))
            : children}
        </View>
      );
    default:
      return <Text style={[styles0.paragraph, alignStyle]}>{children}</Text>;
  }
};

// --- START OF PdfDocument.tsx ---
export const PdfDocument: React.FC<{
  nodes: any[];
  pageOrientation?: 'portrait' | 'landscape';
  pageMargins?: any;
  watermark?: any;
}> = ({
  nodes = [],
  pageOrientation = useConfigStore.getState().pageOrientation,
  pageMargins = useConfigStore.getState().pageMargins,
  watermark
}) => {
  const isLandscape = pageOrientation === 'landscape';
  const size = {
    width: isLandscape ? 11.69 : 8.27,
    height: isLandscape ? 8.27 : 11.69
  };

  const pdfPageSize: [number, number] = [size.width * 72, size.height * 72];

  const marginStyles = {
    paddingTop: pageMargins.top * 72,
    paddingRight: pageMargins.right * 72,
    paddingBottom: pageMargins.bottom * 72,
    paddingLeft: pageMargins.left * 72
  };

  return (
    <Document>
      <Page size={pdfPageSize} style={[styles0.page, marginStyles]} wrap>
        {nodes.map((node, index) => (
          <PdfBlock key={index} node={node} />
        ))}
        {watermark?.text && (
          <Text
            style={{
              position: 'absolute',
              top: '50vh',
              left: '10vw',
              width: '80vw',
              transform:
                watermark.layout === 'diagonal' ? 'rotate(-45deg)' : undefined,
              fontSize: watermark.fontSize,
              color: watermark.color,
              opacity: watermark.opacity,
              textAlign: 'center',
              zIndex: -1
            }}
            fixed
          >
            {watermark.text}
          </Text>
        )}
      </Page>
    </Document>
  );
};
